/**
 * Whether the dry run a host confirmed still describes the send about to run.
 *
 * The dry run is keyed to the composer, not to the guest list. Nothing the host
 * touches changes who is on the roster, so a co-host importing twenty guests in
 * another Studio tab moves the audience without disarming anything here, and the
 * API re-reads the roster when the send lands. The per-channel numbers in the
 * confirmation are read as a promise — "41 text messages" has to mean 41 — so
 * the send counts again first and refuses out loud on any difference.
 *
 * Lives in its own module because it is the guard, not the copy: it must stay
 * testable in `node` (this package has no render harness) and it must not grow
 * into the channel arithmetic it reuses.
 */
import {
  CHANNEL_ORDER,
  channelCountPhrase,
  shortChannelLabel,
  summarizeBroadcastAudience,
} from './broadcastChannels';
import type { SendGroupBroadcastResponse } from '../types/groups';

/** The fields either dry run has to carry for the two to be comparable. */
type BroadcastRun = Pick<
  SendGroupBroadcastResponse,
  'channels' | 'channelPlans' | 'results' | 'requestedCount'
>;

function targeted(run: BroadcastRun): number {
  return Number.isFinite(run.requestedCount) ? run.requestedCount : 0;
}

/**
 * What moved between the two runs, in the host's terms, or null when they
 * describe the same send.
 *
 * Every difference is stated per channel — a roster change that adds twenty
 * phone numbers and no email addresses is a change to the text and not to the
 * mail, and a host who confirmed "41 text messages" is owed that distinction.
 */
export function describeRosterDrift(
  confirmed: BroadcastRun,
  latest: BroadcastRun,
  requested: readonly string[],
): string | null {
  const before = summarizeBroadcastAudience(confirmed, requested);
  const after = summarizeBroadcastAudience(latest, requested);
  const parts: string[] = [];

  if (targeted(confirmed) !== targeted(latest)) {
    parts.push(`${targeted(confirmed)} → ${targeted(latest)} guests targeted`);
  }
  for (const channel of CHANNEL_ORDER) {
    const was = before.plans.find((plan) => plan.channel === channel);
    const now = after.plans.find((plan) => plan.channel === channel);
    if (!was && !now) continue;
    // A channel reported in one run and not the other is a difference we cannot
    // measure, which is the case that most needs stopping rather than the least.
    if (!was || !now) {
      parts.push(`no comparable count for ${shortChannelLabel(channel)}`);
      continue;
    }
    if (was.reachable !== now.reachable) {
      parts.push(`${was.reachable} → ${channelCountPhrase(now)}`);
    }
  }

  if (parts.length === 0) return null;
  return `The guest list changed since your preview: ${parts.join(', ')}. `
    + 'Nothing was sent — the numbers above are current, so confirm again to send '
    + 'to the list as it stands.';
}

/**
 * A fingerprint of the roster the dry run counted.
 *
 * Folded into the composer's key so a refetch that changes the guest list
 * disarms Send exactly as an edit to the subject line does. It covers only what
 * the send actually reads: who is on the list, what we can reach them on, and
 * the two fields the audience filter uses. This closes the same-tab case; the
 * re-count at send time is what closes the other-tab one.
 */
export function rosterFingerprint(
  invitees: readonly {
    id: string;
    email?: string | null;
    phone?: string | null;
    rsvpStatus?: string | null;
    segment?: string | null;
  }[],
): string {
  return (invitees ?? [])
    .map((invitee) => [
      invitee.id,
      invitee.email ?? '',
      invitee.phone ?? '',
      invitee.rsvpStatus ?? '',
      invitee.segment ?? '',
    ].join(':'))
    .sort()
    .join('|');
}
